import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import moment from "moment";
import nationsWithCodes from "app/auth/models/nations-with-codes";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import { ICustomerForm, ICustomerReferentList, ICustomerType } from "../../../customer.interface";
import { ICity } from "app/auth/models/cities";
import { UtilsService } from "../../../../../auth/service/utils.service";
import { AuthenticationService } from "../../../../../auth/service";
import { CustomerService } from "../../../customer.service";
import { CitiesService } from "../../../../../auth/service/cities.service";
import { ListService } from "../../../../../auth/service/list.service";
import { environment } from "../../../../../../environments/environment";
import fieldsInvoice from "../../../form-fields.json";

@Component({
  selector: "app-customers-edit-form",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./customers-edit-form.component.html",
  styleUrls: ["./customers-edit-form.component.scss"],
})
export class CustomersEditFormComponent implements OnInit {
  public formGroupCustomer: FormGroup;
  public nationsWithCodes: { name: string; code: string }[] = nationsWithCodes;
  public formGroupSite: FormGroup;
  public formGroupReferent: FormGroup;
  public contentHeader: IContentHeader = null;
  public customer_id: number;
  public customer: any;
  public logoBase64: string = "";
  public addresses: any = [];
  public fileName: string = "";
  public active_table: string = "customer";
  public addressesList: any = [];
  public referentList: ICustomerReferentList[] = [];
  public isEditModeSite: boolean = false;
  public isEditModeReferent: boolean = false;
  public selectedEditSite: any;
  public selectedEditReferent: any;
  public previousSiteAddress: any = {};
  private showErrors: boolean = false;
  public subjectTypeList: ICustomerType[] = [];
  public fieldsInvoice: any = fieldsInvoice.invoice;
  public fieldsPersonaFisica: any = fieldsInvoice.person;
  private subjectTypePerson: ICustomerType | undefined;

  constructor(
    public _utilService: UtilsService,
    public _activatedRoute: ActivatedRoute,
    public _authenticationService: AuthenticationService,
    public customerService: CustomerService,
    private _formBuilder: FormBuilder,
    private router: Router,
    public _citiesService: CitiesService,
    private _listService: ListService,
  ) {
    this._activatedRoute.paramMap.subscribe(async (params: ParamMap) => {
      const customerId: any = params.get("id");

      if (customerId) {
        if (!this._authenticationService.hasCapability("edit_customer")) {
          this._utilService.hideLoading();
          await this.router.navigateByUrl("/dashboard/main");
          return false;
        }
        this._utilService.disableAutoHideLoading();
        this.customer_id = customerId;
      }
    });

    this.contentHeader = {
      headerTitle: "",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "CUSTOMERS",
            isLink: false,
          },
          {
            name: "LIST",
            isLink: true,
            link: "/dashboard/customers",
          },
          {
            name: "EDIT",
            isLink: false,
          },
        ],
      },
    };

    this.formGroupCustomer = this._formBuilder.group({
      name: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      pec: new FormControl("", [Validators.email]),
      email: new FormControl("", [Validators.email]),
      country: new FormControl(""),
      subject_type: new FormControl(null, [Validators.required]),
    });

    for (const field of [...fieldsInvoice.invoice, ...fieldsInvoice.person] as any) {
      let validators = [];
      if (field.validators?.required) {
        validators.push(Validators.required);
      }
      if (field.validators?.minlength) {
        validators.push(Validators.minLength(field.validators.minlength));
      }
      if (field.validators?.maxLength) {
        validators.push(Validators.maxLength(field.validators.maxLength));
      }

      if (field.type === "email") {
        validators.push(Validators.email);
      }

      this.formGroupCustomer.addControl(field.key, new FormControl(null, validators));
    }

    this.formGroupSite = this._formBuilder.group({
      client: new FormControl("", [Validators.required, Validators.min(0)]),
      label: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      street: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      zipcode: new FormControl("", [Validators.required, Validators.minLength(4), Validators.maxLength(5)]),
      city: new FormControl("", [Validators.required, Validators.min(1)]),
      external_city: new FormControl("", []),
      province: new FormControl("", [Validators.required, Validators.min(1)]),
      km: new FormControl(0, [Validators.required, Validators.min(0)]),
      external: new FormControl(false),
      country: new FormControl(null),
      head_office: new FormControl(false),
    });

    this.formGroupReferent = this._formBuilder.group({
      client: new FormControl(null, [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      site: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      firstname: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      lastname: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      email: new FormControl("", [Validators.required, Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]),
      phone: new FormControl("", [Validators.minLength(9), Validators.maxLength(15)]),
    });
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("edit_customer")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    this.subjectTypeList = await this._listService.getList("customer_type");
    this.subjectTypePerson = this.subjectTypeList.find((item: ICustomerType) => item.name.toLowerCase().includes("persona fisica"));
    await this.loadCustomerData();
  }

  private async loadCustomerData() {
    this.resetSiteForm();
    this.resetReferentForm();
    await this.customerService.getAddresses();
    await this.customerService.getReferents();
    this.customer = await this.customerService.getCustomer(this.customer_id);
    if (this.customer.logo) {
      this.customer.logo = environment.CDN_URL + "/" + this.customer.logo;
    }
    this.contentHeader.headerTitle = this.customer.name;

    this.formGroupCustomer.patchValue({
      name: this.customer.name,
      pec: this.customer.pec,
      email: this.customer.email,
      country: this.customer.country,
      subject_type: this.customer.subject_type,
    });

    if (this.customer.person_birth_date) {
      this.customer.person_birth_date = moment(this.customer.person_birth_date).format("YYYY-MM-DD");
    }

    await this.changeSubjectType();

    this.formGroupSite.patchValue({ client: this.customer.id });
    this.formGroupReferent.patchValue({ client: this.customer.id });

    this.addressesList = this.customerService.addresses.filter((address: any) => address?.client?.id === this.customer.id);

    this.referentList = this.customerService.referencesList.filter((referent: ICustomerReferentList) => referent?.client?.id === this.customer.id);

    const fields: string[] = this.fieldsInvoice.map((field: any) => field.key);
    const basicFields: string[] = Object.keys(this.formGroupCustomer.controls).filter((field: any) => !fields.includes(field));

    fields.map((field: string) => this.formGroupCustomer.get(field).setValue(this.customer[field]));
    basicFields.map((field: string) => this.formGroupCustomer.get(field).setValue(this.customer[field]));

    if (fields.includes("person_birth_city")) {
      this.formGroupCustomer.patchValue({ person_birth_city: this.customer.person_birth_city_id });
    }

    this._utilService.hideLoading();
  }

  async changeSubjectType() {
    this._utilService.showLoading();
    if (this.formGroupCustomer.controls.subject_type.value === this.subjectTypePerson.id) {
      this.fieldsInvoice = [...this.fieldsInvoice, ...this.fieldsPersonaFisica];
    } else {
      this.fieldsInvoice = fieldsInvoice.invoice;
    }

    this.fieldsInvoice = await this.customerService.fillDynamicFields(this.fieldsInvoice);
    this._utilService.hideLoading();
  }

  changeTab(tab: string) {
    this.active_table = this.active_table === tab ? "" : tab;
  }

  fieldHasErrors(field_name: string): boolean {
    if (this.showErrors) {
      return this.formGroupCustomer?.controls[field_name]?.status === "INVALID" || this.formGroupSite?.controls[field_name]?.status === "INVALID" || this.formGroupReferent?.controls[field_name]?.status === "INVALID";
    } else {
      return (
        (this.formGroupCustomer?.controls[field_name]?.status === "INVALID" && this.formGroupCustomer?.controls[field_name]?.touched) ||
        (this.formGroupSite?.controls[field_name]?.status === "INVALID" && this.formGroupSite?.controls[field_name]?.touched) ||
        (this.formGroupReferent?.controls[field_name]?.status === "INVALID" && this.formGroupReferent?.controls[field_name]?.touched)
      );
    }
  }

  /*In caso di sede estera città zipcode e provincia saranno impostati e inviati a null*/
  manageExternalSite() {
    if (this.formGroupSite.value.external) {
      this.formGroupSite.patchValue({
        zipcode: null,
        city: null,
        province: null,
        km: 1,
      });
      this.formGroupSite.get("country").addValidators(Validators.required);
      this.formGroupSite.get("external_city").addValidators(Validators.required);
    } else {
      this.formGroupSite.patchValue({
        zipcode: null,
        city: null,
        province: null,
        km: null,
      });
      this.formGroupSite.get("country").clearValidators();
      this.formGroupSite.get("external_city").clearValidators();
    }
  }

  async edit() {
    if (!this._authenticationService.hasCapability("edit_customer")) {
      window.history.back();
      return false;
    }
    this.showErrors = true;
    // controllo valori
    if (!this.formGroupCustomer.value.fiscalcode && !this.formGroupCustomer.value.vat_number) {
      await this._utilService.showSWALError({ text: "INSERT_FISCALCODE_OR_VAT_NUMBER" });
      return false;
    }
    if (this.formGroupCustomer.errors !== null) {
      return false;
    }

    let data: ICustomerForm = this.formGroupCustomer.value;

    data.name = String(data.name).trim();
    data.business_name = String(data.business_name).trim();

    if (data.subject_type !== this.subjectTypePerson.id) {
      for (const key of fieldsInvoice.person.map((field: any) => field.key)) {
        data[key] = null;
      }
    }

    this.active_table = "customer";
    this._utilService.showLoading();

    if (this.logoBase64.length > 0) {
      const fileExtension: string = this.logoBase64.substring("data:image/".length, this.logoBase64.indexOf(";base64"));
      const fileName: string = `logo-${this._utilService.slugify_me(data.name)}-${moment().valueOf()}.${fileExtension}`;
      const oldUrl: string = this.customer && this.customer.logo ? this.customer?.logo.split("/").pop() : null;
      const uploadFile = await this._utilService.upload_media("public", fileName, this.logoBase64, "public$$$" + oldUrl);
      if (uploadFile) {
        data.logo = "public/" + fileName;
      } else {
        await this._utilService.showSWALError({ text: "ERROR_UPDATE_CLIENT_LOGO" });
      }
    }

    return this.customerService
      .editCustomer(this.customer_id, data)
      .then(async () => {
        this._utilService.hideLoading();
        this.showErrors = false;
        await this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_CLIENT" });
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_EDIT_CLIENT" });
      });
  }

  //Fine funzioni dati cliente

  //Funzioni sedi
  resetSiteForm() {
    this.formGroupSite.reset();
    this.isEditModeSite = false;
    this.selectedEditSite = null;
    this.previousSiteAddress = {};
    this.formGroupSite.patchValue({
      km: 0,
    });
  }

  enterEditModeSite(site: any) {
    this.isEditModeSite = true;
    this.selectedEditSite = site;
    this.formGroupSite.patchValue({
      label: site.label,
      km: site.km,
      street: site.street,
      zipcode: site.zipcode,
      city: site.city_id,
      province: site.province_id,
      external: site.external,
      country: site.country,
      head_office: site.head_office,
      external_city: site.external_city,
    });
    if (site.external) {
      this.formGroupSite.get("country").addValidators(Validators.required);
      this.formGroupSite.get("external_city").addValidators(Validators.required);
    } else {
      this.formGroupSite.get("country").clearValidators();
      this.formGroupSite.get("external_city").clearValidators();
    }
    this.previousSiteAddress = this.formGroupSite.value;
  }

  setAutoProvince() {
    const cityValue: number = this.formGroupSite.get("city").value;
    if (cityValue > 0) {
      const cityObj: ICity = this._citiesService.list.find((city: ICity) => city.id === cityValue);
      const province = this._citiesService.list.find((city: ICity) => city.initials === cityObj.initials && city.is_main === 1);
      this.formGroupSite.patchValue({ province: province.id });
    }
  }

  async addSite() {
    this.formGroupSite.patchValue({
      client: this.customer.id,
    });
    if (!this._authenticationService.hasCapability("create_customer_address")) {
      window.history.back();
      return false;
    }
    this.showErrors = true;
    // controllo valori
    if (this.formGroupSite.invalid) {
      return false;
    }
    this._utilService.showLoading();
    await this.customerService
      .addAddress(this.formGroupSite.value)
      .then(async (response) => {
        this.showErrors = false;
        //In caso il valore di km sia a ZERO calcoliamo in automatico la distanza della sede dalla nostra
        if (this.formGroupSite.value.km === 0) {
          await this.customerService
            .calculateDistanceForCustomerToUs(response.site_id)
            .catch(async () => {
              this._utilService.hideLoading();
              await this._utilService.showSWALError({ text: "ERROR_CALCULATE_DISTANCE_CUSTOMER" });
            });
        }
        await this.loadCustomerData();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_ADD_CUSTOMER_SITE" });
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_ADD_CUSTOMER_SITE" });
      });
  }

  async editSite() {
    this.formGroupSite.patchValue({
      client: this.customer.id,
    });
    if (!this._authenticationService.hasCapability("edit_customer_address")) {
      window.history.back();
      return false;
    }
    // controllo valori
    if (!this.formGroupSite.value.external) {
      if (this.formGroupSite.invalid) {
        return this._utilService.showSWALError({ text: "SOME_DATA_IS_INVALID" });
      }
    } else {
      if (!this.formGroupSite.value.client || !this.formGroupSite.value.label || !this.formGroupSite.value.street) {
        return false;
      }
    }
    let data: any = this.formGroupSite.value;
    this._utilService.showLoading();
    return this.customerService
      .editAddress(this.selectedEditSite.id, data)
      .then(async () => {
        //In caso il valore di km sia a ZERO calcoliamo in automatico la distanza della sede dalla nostra
        if (data.km === 0) {
          await this.customerService
            .calculateDistanceForCustomerToUs(this.selectedEditSite.id)
            .catch(async () => {
              this._utilService.hideLoading();
              await this._utilService.showSWALError({ text: "ERROR_CALCULATE_DISTANCE_CUSTOMER" });
            });
        } else {
          //Controllo se i dati della sede siano cambiati, se si cambiamo il calcolo della distanza
          if (
            data.street !== this.previousSiteAddress.street ||
            data.city !== this.previousSiteAddress.city ||
            data.province !== this.previousSiteAddress.province ||
            (data.zipcode !== this.previousSiteAddress.zipcode && this.previousSiteAddress.km === data.km)
          ) {
            await this.customerService
              .calculateDistanceForCustomerToUs(this.selectedEditSite.id)
              .catch(async () => {
                this._utilService.hideLoading();
                await this._utilService.showSWALError({ text: "ERROR_CALCULATE_DISTANCE_CUSTOMER" });
              });
          }
        }
        await this.loadCustomerData();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_UPDATE_CUSTOMER_SITE" });
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_UPDATE_CUSTOMER_SITE" });
      });
  }

  async deleteSite(address: any) {
    let deleteText: string = await this._utilService.translate("ALERT.CONFIRM_DELETE_TEXT");
    deleteText += await this._utilService.translate("ALERT.CONFIRM_DELETE_THIS_OBJECT");
    deleteText += ` ${address.label}`;

    await this._utilService
      .showSWALDeleteConfirmation({ text: deleteText })
      .then(async (result) => {
        if (result.value) {
          this._utilService.showLoading();
          await this.customerService
            .deleteAddress(address.id)
            .then(async () => {
              this.addressesList = this.customerService.addresses.filter((address: any) => address?.client?.id === this.customer.id);
              this._utilService.hideLoading();
              await this._utilService.showSWALSuccess({ text: "SUCCESS_DELETE_CUSTOMER_SITE" });
            })
            .catch(async () => {
              this._utilService.hideLoading();
              await this._utilService.showSWALError({ text: "ERROR_DELETE_CUSTOMER_SITE" });
            });
        } else if (result.isDismissed) {
          this._utilService.hideLoading();
          await this._utilService.showSWALError({
            title: "ABORTED_TITLE",
            text: "ABORTED_TEXT",
          });
        }
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_DELETE_CUSTOMER_SITE" });
      });
  }

  //Fine funzioni sedi

  //Funzioni Referenti
  enterEditModeReferent(referent: any) {
    this.isEditModeReferent = true;
    this.selectedEditReferent = referent;
    this.formGroupReferent.patchValue({
      site: referent.addresses[0].id,
      firstname: referent.firstname,
      lastname: referent.lastname,
      email: referent.email,
      phone: referent.phone,
    });
  }

  resetReferentForm() {
    this.formGroupSite.patchValue({
      km: 0,
    });
    this.formGroupReferent.reset();
    this.isEditModeReferent = false;
    this.selectedEditReferent = null;
  }

  async addReferent() {
    this.formGroupReferent.patchValue({
      client: this.customer.id,
    });
    this.showErrors = true;
    // controllo valori
    if (this.formGroupReferent.invalid) {
      return false;
    }
    this._utilService.showLoading();
    await this.customerService
      .addReferent(this.formGroupReferent.value)
      .then(async () => {
        this.showErrors = false;
        await this.loadCustomerData();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_ADD_CUSTOMER_REFERENT" });
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_ADD_CUSTOMER_REFERENT" });
      });
  }

  async editReferent() {
    this.formGroupReferent.patchValue({
      client: this.customer.id,
    });
    if (this.formGroupReferent.invalid) {
      return this._utilService.showSWALError({ text: "SOME_DATA_IS_INVALID" });
    }
    this._utilService.showLoading();
    await this.customerService
      .editReferent(this.selectedEditReferent.id, this.formGroupReferent.value)
      .then(async () => {
        this.showErrors = false;
        await this.loadCustomerData();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_CUSTOMER_REFERENT" });
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_EDIT_CUSTOMER_REFERENT" });
      });
  }

  async deleteReferent(referent: any) {
    if (!this._authenticationService.hasCapability("delete_customer_referent")) {
      window.history.back();
      return false;
    }
    let deleteText: string = await this._utilService.translate("ALERT.CONFIRM_DELETE_TEXT");
    deleteText += await this._utilService.translate("ALERT.CONFIRM_DELETE_THIS_OBJECT");
    deleteText += ` ${referent.complete_name}`;
    await this._utilService
      .showSWALDeleteConfirmation({ text: deleteText })
      .then(async (result) => {
        if (result.value) {
          this._utilService.showLoading();
          await this.customerService
            .deleteReferent(referent.id)
            .then(async () => {
              this.referentList = this.referentList.filter((item) => item.id != referent.id);
              this._utilService.hideLoading();
              await this._utilService.showSWALSuccess({ text: "SUCCESS_DELETE_CUSTOMER_REFERENT" });
            })
            .catch(async () => {
              this._utilService.hideLoading();
              await this._utilService.showSWALError({ text: "ERROR_DELETE_CUSTOMER_REFERENT" });
            });
        } else if (result.isDismissed) {
          this._utilService.hideLoading();
          await this._utilService.showSWALError({
            title: "ABORTED_TITLE",
            text: "ABORTED_TEXT",
          });
        }
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_DELETE_CUSTOMER_REFERENT" });
      });
  }

  //Fine funzioni referenti
}
